import { useCallback, useMemo, useState } from 'react';
import { ACTIVITY_SUGGESTIONS_SEARCH_THRESHOLD } from 'constants/general';
import { useAppSelectorShallow } from 'store/hooks';
import { attachActivityToSuggestion } from 'utils/habit-suggestion.util';
import {
  getHabitPackActivitiesWithPackTitle,
  getMatchedActivities
} from 'utils/settings.util';
import { ExtendedSuggestion, Suggestion } from 'interfaces/setting.interface';

const dedupeSuggestions = (
  suggestions: ExtendedSuggestion[]
): ExtendedSuggestion[] =>
  suggestions.filter(
    (activity, index, self) =>
      index ===
      self.findIndex(
        (item) =>
          item.name === activity.name && item.duration === activity.duration
      )
  );

export const useHabitNameSuggestions = (activityType?: string) => {
  const { activityLibrary, marketplaceApprovedPacks } = useAppSelectorShallow(
    (state) => ({
      activityLibrary: state.setting.activityLibrary,
      marketplaceApprovedPacks: state.habitPack.marketplaceApprovedPacks
    })
  );

  const [suggestions, setSuggestions] = useState<ExtendedSuggestion[]>([]);

  const marketplaceActivities = useMemo(
    () =>
      getHabitPackActivitiesWithPackTitle(
        marketplaceApprovedPacks,
        activityType
      ),
    [activityType, marketplaceApprovedPacks]
  );

  const updateSuggestions = useCallback(
    (search: string) => {
      if (search.length <= ACTIVITY_SUGGESTIONS_SEARCH_THRESHOLD) {
        setSuggestions([]);
        return;
      }

      const matched = getMatchedActivities(
        marketplaceActivities,
        activityLibrary,
        search,
        search
      );

      const matchedWithActivities = matched.map((suggestion: Suggestion) =>
        attachActivityToSuggestion(
          suggestion,
          activityLibrary,
          marketplaceActivities
        )
      );

      setSuggestions(dedupeSuggestions(matchedWithActivities));
    },
    [activityLibrary, marketplaceActivities]
  );

  const clearSuggestions = useCallback(() => {
    setSuggestions([]);
  }, []);

  return {
    suggestions,
    updateSuggestions,
    clearSuggestions
  };
};
